<template>
  <v-dialog
    v-model="dialog"
    width="850"
    :fullscreen="$vuetify.display.smAndDown"
    persistent
  >
    <template #activator="{ props }">
      <slot
        name="activator"
        v-bind="{ props, status, loading }"
      />
    </template>
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar color="primary">
          <v-toolbar-title>
            {{ $t('connection-manager.proven-expert.dialog-title') }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              @click="dialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>
      <v-card-text>
        <ProvenExpertLinkInput
          :company-id="companyId"
          @success="handleSaved"
        />
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <p class="text-primary font-weight-bold">
              {{ $t('connection-manager.proven-expert.choose-title') }}
            </p>
            <p class="mb-0">
              {{ $t('connection-manager.proven-expert.choose-description') }}
            </p>
            <a
              v-if="$t('connection-manager.proven-expert.learnMoreUrl')"
              :href="$t('connection-manager.proven-expert.learnMoreUrl')"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('connection-manager.proven-expert.learnMore') }}
            </a>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="d-flex flex-column ga-2"
          >
            <div class="d-flex justify-space-between align-center w-full border px-4">
              <p class="text-body-2 mb-0">
                {{ $t('connection-manager.proven-expert.include-in-signature') }}
              </p>
              <v-switch
                v-model="includeInSignature"
                :loading="toggleLoading"
                :disabled="!localProvenExpertLink"
                color="primary"
                density="compact"
                hide-details
              />
            </div>
            <div class="d-flex justify-space-between align-center w-full border px-4">
              <p class="text-body-2 mb-0">
                {{ $t('connection-manager.proven-expert.include-in-contact-details') }}
              </p>
              <v-switch
                v-model="includeInContactDetails"
                :loading="contactDetailsLoading || toggleLoading"
                :disabled="!localProvenExpertLink"
                color="primary"
                density="compact"
                hide-details
              />
            </div>
            <p class="mb-0 text-info">
              {{ $t('connection-manager.proven-expert.note') }}
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import * as Sentry from '@sentry/vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { computed, inject, onMounted, ref, watch } from 'vue'

import type { AuthInstance } from '@/auth'
import ProvenExpertLinkInput from '@/components/ProvenExpertLinkInput.vue'
import GET_PROVEN_EXPERT_SETTINGS from '@/modules/settings/emailSettings/queries/GetProvenExpertSettings.gql'
import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_PROVEN_EXPERT from '@/modules/settings/emailSettings/queries/UpdateCompanyAmbassadorSignatureIncludeProvenExpert.gql'
import UPDATE_PROVEN_EXPERT_ON_LANDINGPAGE from '@/modules/settings/emailSettings/queries/UpdateProvenExpertOnLandingpage.gql'
import CompanyQuery from '@/modules/settings/functionalSettings/queries/Company.gql'

type ConnectionStatus = 'not-connected' | 'link-connected' | 'link-active'

const $auth = inject<AuthInstance>('auth')
const companyId = computed(() => $auth?.user?.companyId ?? '')

const dialog = ref(false)
const localProvenExpertLink = ref('')
const toggleLoading = ref(false)
const localShowOnLandingpage = ref(false)
const localIncludeInSignature = ref(false)

const { load: loadSettings, onResult: onSettingsResult } = useLazyQuery(
  GET_PROVEN_EXPERT_SETTINGS,
  () => ({ companyId: companyId.value })
)

onSettingsResult(({ data }) => {
  if (data?.settings) {
    localProvenExpertLink.value = data.settings.company.provenExpertLink ?? ''
    localShowOnLandingpage.value = data.settings.company.showProvenExpertOnLandingpage ?? false
  }
})

const {
  result: companyResult,
  load: loadCompany,
  refetch: refetchCompany,
} = useLazyQuery(CompanyQuery, () => ({
  id: companyId.value,
}))

watch(
  () => companyResult.value,
  (value) => {
    const data = value?.data ?? value
    localIncludeInSignature.value =
      data?.company?.ambassador?.signature?.includeProvenExpert ?? false
  },
  { immediate: true }
)

const { mutate: updateIncludeProvenExpert } = useMutation(
  UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_PROVEN_EXPERT
)

const { mutate: updateShowOnLandingpage, loading: contactDetailsLoading } = useMutation(
  UPDATE_PROVEN_EXPERT_ON_LANDINGPAGE
)

const includeInSignature = computed({
  get: () => localIncludeInSignature.value,
  set: (val) => updateToggle(val),
})

const includeInContactDetails = computed({
  get: () => localShowOnLandingpage.value,
  set: (val) => updateContactDetailsToggle(val),
})

const status = computed<ConnectionStatus>(() => {
  if (!localProvenExpertLink.value) {
    return 'not-connected'
  }
  if (localIncludeInSignature.value || localShowOnLandingpage.value) {
    return 'link-active'
  }
  return 'link-connected'
})

const loading = computed(() => false)

watch(dialog, (isOpen) => {
  if (isOpen && companyId.value) {
    loadSettings()
    loadCompany()
  }
})

onMounted(() => {
  if (companyId.value) {
    loadSettings()
    loadCompany()
  }
})

const handleSaved = async (reviewLink: string) => {
  localProvenExpertLink.value = reviewLink
  if (!reviewLink) {
    await updateToggle(false)
    await updateContactDetailsToggle(false)
  }
}

const updateToggle = async (value: boolean) => {
  if (!companyId.value) return
  toggleLoading.value = true
  try {
    await updateIncludeProvenExpert({
      input: {
        companyId: companyId.value,
        includeProvenExpert: value,
      },
    })
    localIncludeInSignature.value = value
    await refetchCompany()
  } finally {
    toggleLoading.value = false
  }
}

const updateContactDetailsToggle = async (value: boolean) => {
  if (!companyId.value) return
  try {
    toggleLoading.value = true
    await updateShowOnLandingpage({
      input: {
        companyId: companyId.value,
        showProvenExpertOnLandingpage: value,
      },
    })
    localShowOnLandingpage.value = value
  } catch (error) {
    Sentry.captureException(error)
  } finally {
    toggleLoading.value = false
  }
}
</script>
